import { createStore } from "zustand/vanilla"
import type { ChatRefusal, ConnState, Conversation, Message } from "../types"
import type { Jid } from "../xmpp/jid"

/**
 * A message that arrived while the reader was away, due an OS notification
 * (FAME-858). Held rather than fired: the name and routing live in the React
 * tree, so `MessageNotifier` fires it and removes it by `id`, like `refusal`.
 */
export type PendingNotification = {
  /** The message id — unique per notification, and what removes it. */
  id: string
  /** The conversation — the notification's title and route. */
  peer: Jid
  /** The sender's handle, for a group body's prefix. */
  author: string
  body: string
}

/** What the UI reads. These three setters are the ones the session calls. */
export type ChatState = {
  connState: ConnState
  /** How long a sender may still retract, per the server (FAME-522). */
  retractionWindowHours?: number
  /** Our own JID, from `/auth`. What tells our messages from a peer's. */
  selfJid?: Jid
  /** The conversation on screen. Drives unread suppression. */
  activePeer?: Jid
  conversations: Conversation[]
  messagesByPeer: Partial<Record<Jid, Message[]>>
  /** A group the server just said we are not a Member of, until the view steps back from it. */
  removedFrom?: Jid
  /** Peers whose history has come back, so an empty thread is known to be empty. */
  historyLoaded: Partial<Record<Jid, true>>
  /** Handles typing in each conversation right now, in the order they started. */
  typing: Partial<Record<Jid, string[]>>
  /**
   * The last thing the server refused, or nothing to report. Held rather than
   * shown: the store deals in what happened, the UI in what to say about it,
   * and it clears once that has been said.
   */
  refusal?: ChatRefusal
  /** Messages due an OS notification, oldest first; `MessageNotifier` drains them. */
  pendingNotifications: PendingNotification[]

  /** What `POST /auth` told us about this session. */
  setConfig: (config: { selfJid: Jid; retractionWindowHours: number }) => void
  setConnState: (state: ConnState) => void
  reset: () => void
}

const initialState: Pick<
  ChatState,
  | "connState"
  | "conversations"
  | "messagesByPeer"
  | "historyLoaded"
  | "typing"
  | "pendingNotifications"
> = {
  connState: "disconnected",
  conversations: [],
  messagesByPeer: {},
  historyLoaded: {},
  typing: {},
  pendingNotifications: [],
}

export function createChatStore() {
  return createStore<ChatState>()((set) => ({
    ...initialState,

    setConfig: (config) => set(config),
    setConnState: (connState) => set({ connState }),
    reset: () => set({ ...initialState }),
  }))
}

export type ChatStore = ReturnType<typeof createChatStore>
